import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { EVENTS, LESSONS, MONTHS } from './blueprint';
import { MetaShell } from './MetaShell';
import { int, kr } from './model';

/*
 * Meta → Historikken. May → today in one picture: what we spent, what it
 * brought in, the moments that changed the curve, and what we learned. All
 * hand-curated from the pricing study and the vault (see blueprint.ts).
 */

export function MetaHistoryPage() {
  const totalSpend = MONTHS.reduce((n, m) => n + m.spendNok, 0);
  const totalMembers = MONTHS.reduce((n, m) => n + m.newMembers, 0);
  return (
    <MetaShell
      title="Hva som skjedde"
      lead={
        <>
          Fra mai til september brukte vi {kr(totalSpend)} på annonser og fikk {int(totalMembers)} nye
          medlemmer. I oktober var 148 aktive. Folk sluttet nesten like fort som de kom, og fra
          august målte vi på feil ting.
        </>
      }
    >
      <Card className="overflow-x-auto p-3 sm:p-4">
        <MonthChart />
      </Card>

      <div className="grid gap-3 lg:grid-cols-[1.2fr_1fr]">
        <Card className="p-3 sm:p-4">
          <h2 className="mb-3 text-section text-fg">Øyeblikkene som endret kurven</h2>
          <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
            {EVENTS.map((e) => (
              <li key={e.date + e.title} className="relative">
                <span
                  className={cn(
                    'absolute -left-[21.5px] top-1.5 size-2.5 rounded-full ring-4 ring-surface',
                    e.tone === 'good' ? 'bg-good' : e.tone === 'bad' ? 'bg-bad' : 'bg-fg-subtle',
                  )}
                />
                <div className="flex items-baseline gap-2">
                  <span className="tabular text-label text-fg-subtle">{e.date}</span>
                  <span className="text-body font-medium text-fg">{e.title}</span>
                </div>
                <p className="text-label text-fg-muted">{e.body}</p>
              </li>
            ))}
          </ol>
        </Card>
        <Card className="p-3 sm:p-4">
          <h2 className="mb-3 text-section text-fg">Seks lærdommer</h2>
          <ol className="flex flex-col gap-3">
            {LESSONS.map((l, i) => (
              <li key={l.title} className="flex gap-3">
                <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-3 text-label font-semibold text-fg">
                  {i + 1}
                </span>
                <div>
                  <div className="text-body font-medium text-fg">{l.title}</div>
                  <div className="text-label text-fg-muted">{l.body}</div>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </MetaShell>
  );
}

function MonthChart() {
  const W = 760;
  const H = 260;
  const L = 50;
  const R = 50;
  const T = 30;
  const B = 40;
  const iw = W - L - R;
  const ih = H - T - B;
  const maxSpend = Math.max(...MONTHS.map((m) => m.spendNok));
  const maxCount = Math.max(...MONTHS.map((m) => Math.max(m.bookings, m.newMembers)));
  const bw = iw / MONTHS.length;
  const x = (i: number) => L + bw * i + bw / 2;
  const yS = (v: number) => T + ih - (v / maxSpend) * ih;
  const yC = (v: number) => T + ih - (v / maxCount) * ih;
  const line = (key: 'bookings' | 'newMembers') =>
    MONTHS.map((m, i) => `${i ? 'L' : 'M'} ${x(i)} ${yC(m[key])}`).join(' ');

  return (
    <div className="min-w-[620px]">
      <div className="mb-2 flex flex-wrap gap-4 text-label text-fg-muted">
        <span className="flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-[var(--accent-spend)]/40" /> Brukt på annonser (kr)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-[var(--accent-booking)]" /> Bookinger (alle kanaler)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-[var(--accent-close)]" /> Nye medlemmer
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="block h-auto w-full" role="img" aria-label="Forbruk, bookinger og nye medlemmer per måned">
        {MONTHS.map((m, i) => (
          <g key={m.month}>
            <rect
              x={x(i) - bw * 0.3}
              y={yS(m.spendNok)}
              width={bw * 0.6}
              height={T + ih - yS(m.spendNok)}
              rx={4}
              fill="var(--accent-spend)"
              fillOpacity={0.28}
            />
            <text x={x(i)} y={T + ih - 8} textAnchor="middle" className="fill-[var(--fg-muted)] tabular" fontSize={11}>
              {Math.round(m.spendNok / 1000)}k
            </text>
            <text x={x(i)} y={H - 16} textAnchor="middle" className="fill-[var(--fg-subtle)]" fontSize={12}>
              {m.month}
            </text>
          </g>
        ))}
        <path d={line('bookings')} fill="none" stroke="var(--accent-booking)" strokeWidth={2.5} />
        <path d={line('newMembers')} fill="none" stroke="var(--accent-close)" strokeWidth={2.5} />
        {MONTHS.map((m, i) => (
          <g key={`p-${m.month}`}>
            <circle cx={x(i)} cy={yC(m.bookings)} r={3.5} fill="var(--accent-booking)" />
            <text x={x(i) + 8} y={yC(m.bookings) - 6} className="fill-[var(--accent-booking)] tabular" fontSize={11}>
              {m.bookings}
            </text>
            <circle cx={x(i)} cy={yC(m.newMembers)} r={3.5} fill="var(--accent-close)" />
            <text x={x(i) + 8} y={yC(m.newMembers) + 14} className="fill-[var(--accent-close)] tabular" fontSize={11}>
              {m.newMembers}
            </text>
          </g>
        ))}
        <line x1={L} x2={W - R} y1={T + ih} y2={T + ih} stroke="var(--border-strong)" />
      </svg>
      <p className="mt-1 text-micro text-fg-subtle">
        Kilde: prisstudien (quick-facts, 03.10). Kroner etter korrigert valutakurs. Juli hadde null skjemalead fordi alt gikk til nettsiden.
      </p>
    </div>
  );
}
